import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Docs.css";
import {
  createDoc,
  deleteDoc,
  DocPayload,
  DocRecord,
  fetchDocs,
  updateDoc,
} from "../config/api";

type SelectedDocId = string | "new" | null;

const emptyPayload: DocPayload = {
  title: "",
  body: "",
  tags: [],
};

const buildFormState = (doc?: DocRecord | null): DocPayload => ({
  title: doc?.title ?? "",
  body: doc?.body ?? "",
  tags: doc?.tags ?? [],
});

const Docs: React.FC = () => {
  const navigate = useNavigate();
  const [docs, setDocs] = useState<DocRecord[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<SelectedDocId>(null);
  const [editingDocId, setEditingDocId] = useState<SelectedDocId>(null);
  const [formState, setFormState] = useState<DocPayload>(emptyPayload);
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const selectedDoc = useMemo(() => {
    if (selectedDocId === "new" || !selectedDocId) {
      return null;
    }
    return docs.find((doc) => doc.docId === selectedDocId) ?? null;
  }, [docs, selectedDocId]);

  const isEditing = editingDocId !== null;

  useEffect(() => {
    const loadDocs = async () => {
      try {
        setLoading(true);
        const data = await fetchDocs();
        setDocs(data);
        if (data.length > 0) {
          setSelectedDocId(data[0].docId);
          setFormState(buildFormState(data[0]));
        }
      } catch (err) {
        console.error("SuperadminFE: failed to load docs", err);
        setError("Unable to load docs right now. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    loadDocs();
  }, []);

  const refreshDocs = async (keepSelection?: string) => {
    const data = await fetchDocs();
    setDocs(data);
    if (data.length === 0) {
      setSelectedDocId(null);
      setFormState(emptyPayload);
      return;
    }
    const nextSelection = keepSelection && data.some((doc) => doc.docId === keepSelection)
      ? keepSelection
      : data[0].docId;
    setSelectedDocId(nextSelection);
    const activeDoc = data.find((doc) => doc.docId === nextSelection);
    setFormState(buildFormState(activeDoc));
  };

  const handleSelectDoc = (docId: string) => {
    if (isEditing && editingDocId !== docId) {
      return;
    }
    setError(null);
    setSelectedDocId(docId);
    const doc = docs.find((item) => item.docId === docId);
    setFormState(buildFormState(doc));
    setEditingDocId(null);
    setTagInput("");
  };

  const handleStartEditing = () => {
    if (!selectedDocId) {
      return;
    }
    setError(null);
    setEditingDocId(selectedDocId);
    if (selectedDocId === "new") {
      setFormState(emptyPayload);
    } else if (selectedDoc) {
      setFormState(buildFormState(selectedDoc));
    }
  };

  const handleNewDoc = () => {
    setError(null);
    setSelectedDocId("new");
    setEditingDocId("new");
    setFormState(emptyPayload);
    setTagInput("");
  };

  const handleFormChange = (field: keyof DocPayload, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (!trimmed) {
      return;
    }
    setFormState((prev) => ({
      ...prev,
      tags: Array.from(new Set([...prev.tags, trimmed])),
    }));
    setTagInput("");
  };

  const handleTagKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormState((prev) => ({
      ...prev,
      tags: prev.tags.filter((item) => item !== tag),
    }));
  };

  const handleCancelEdit = () => {
    if (selectedDocId === "new") {
      setSelectedDocId(docs[0]?.docId ?? null);
      setFormState(buildFormState(docs[0] ?? null));
    } else if (selectedDoc) {
      setFormState(buildFormState(selectedDoc));
    }
    setEditingDocId(null);
    setTagInput("");
  };

  const handleSave = async () => {
    if (!formState.title.trim() || !formState.body.trim()) {
      setError("Title and body are required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (selectedDocId === "new") {
        const created = await createDoc({
          title: formState.title.trim(),
          body: formState.body.trim(),
          tags: formState.tags,
        });
        await refreshDocs(created.docId);
      } else if (selectedDocId) {
        await updateDoc(selectedDocId, {
          title: formState.title.trim(),
          body: formState.body.trim(),
          tags: formState.tags,
        });
        await refreshDocs(selectedDocId);
      }
      setEditingDocId(null);
    } catch (err) {
      console.error("SuperadminFE: failed to save doc", err);
      setError("Unable to save the document. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const promptDelete = () => {
    if (!selectedDocId || selectedDocId === "new") {
      return;
    }
    setConfirmDeleteId(selectedDocId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) {
      return;
    }
    try {
      setSaving(true);
      await deleteDoc(confirmDeleteId);
      setConfirmDeleteId(null);
      await refreshDocs();
      setEditingDocId(null);
    } catch (err) {
      console.error("SuperadminFE: failed to delete doc", err);
      setError("Unable to delete the document. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value?: string) => {
    if (!value) {
      return "--";
    }
    try {
      return new Date(value).toLocaleString();
    } catch (error) {
      return value;
    }
  };

  const renderDetail = () => {
    if (loading) {
      return <div className="docs-detail-empty">Loading docs...</div>;
    }
    if (!selectedDocId) {
      return <div className="docs-detail-empty">Select a document to view details.</div>;
    }
    return (
      <div className="docs-detail-card">
        <div className="docs-field">
          <label htmlFor="doc-title">Title</label>
          <input
            id="doc-title"
            type="text"
            value={formState.title}
            onChange={(event) => handleFormChange("title", event.target.value)}
            disabled={!isEditing}
          />
        </div>
        <div className="docs-field">
          <label htmlFor="doc-body">Body</label>
          <textarea
            id="doc-body"
            value={formState.body}
            onChange={(event) => handleFormChange("body", event.target.value)}
            disabled={!isEditing}
            rows={14}
          />
        </div>
        <div className="docs-field">
          <label>Tags</label>
          <div className={`docs-tags ${isEditing ? "editing" : ""}`}>
            <div className="docs-tag-chips">
              {formState.tags.length === 0 && !isEditing && <span className="docs-tag-placeholder">No tags</span>}
              {formState.tags.map((tag) => (
                <span key={tag} className="docs-tag">
                  {tag}
                  {isEditing && (
                    <button type="button" onClick={() => handleRemoveTag(tag)} aria-label={`Remove ${tag}`}>
                      ×
                    </button>
                  )}
                </span>
              ))}
            </div>
            {isEditing && (
              <div className="docs-tag-input">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(event) => setTagInput(event.target.value)}
                  onKeyDown={handleTagKeyDown}
                  placeholder="Add tag and press Enter"
                />
                <button
                  type="button"
                  className="docs-btn docs-btn-secondary"
                  onClick={handleAddTag}
                  disabled={!tagInput.trim()}
                >
                  Add
                </button>
              </div>
            )}
          </div>
        </div>
        {selectedDoc && (
          <div className="docs-meta">
            <span>Created: {formatDate(selectedDoc.createdOn)}</span>
            <span>Updated: {formatDate(selectedDoc.modifiedOn)}</span>
          </div>
        )}
        <div className="docs-action-row">
          {isEditing ? (
            <>
              <button className="docs-btn docs-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                Save
              </button>
              <button className="docs-btn docs-btn-ghost" type="button" onClick={handleCancelEdit}>
                Cancel
              </button>
            </>
          ) : (
            <>
              <button className="docs-btn docs-btn-secondary" type="button" onClick={handleStartEditing} disabled={!selectedDocId}>
                Edit
              </button>
              <button
                className="docs-btn docs-btn-danger"
                type="button"
                onClick={promptDelete}
                disabled={!selectedDocId || selectedDocId === "new"}
              >
                Delete
              </button>
            </>
          )}
        </div>
        {error && <div className="docs-error">{error}</div>}
      </div>
    );
  };

  return (
    <div className="docs-page">
      <header className="docs-header">
        <div className="docs-header-content">
          <div className="docs-logo" onClick={() => navigate("/dashboard")} role="button" tabIndex={0}>
            <img src="/assets/LogotypeSuperAdminHorizontal.svg" alt="Parallel Superadmin" />
          </div>
          <div className="docs-header-actions">
            <button type="button" className="docs-btn docs-btn-ghost" onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
            <button
              type="button"
              className="docs-btn docs-btn-primary"
              onClick={handleNewDoc}
              disabled={isEditing && editingDocId !== "new"}
            >
              New Doc
            </button>
          </div>
        </div>
      </header>

      <main className="docs-main">
        <aside className="docs-list-panel">
          {docs.length === 0 && !loading ? (
            <div className="docs-empty">No docs found. Create your first entry.</div>
          ) : (
            <ul>
              {docs.map((doc) => (
                <li
                  key={doc.docId}
                  className={selectedDocId === doc.docId ? "active" : ""}
                  onClick={() => handleSelectDoc(doc.docId)}
                >
                  <div className="docs-list-title">{doc.title}</div>
                  <div className="docs-list-meta">
                    <span>{formatDate(doc.modifiedOn)}</span>
                    <div className="docs-list-tags">
                      {doc.tags.map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
        <section className="docs-detail-panel">{renderDetail()}</section>
      </main>

      {confirmDeleteId && (
        <div className="docs-modal-overlay" role="dialog" aria-modal="true">
          <div className="docs-modal">
            <h3>Delete document?</h3>
            <p>This action cannot be undone. The document will be removed from the AI knowledge base.</p>
            <div className="docs-modal-actions">
              <button type="button" className="docs-btn docs-btn-ghost" onClick={() => setConfirmDeleteId(null)}>
                Cancel
              </button>
              <button type="button" className="docs-btn docs-btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Docs;
